import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Network } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AgreementSelect } from "@/components/red/ReferidoPrompt";

const money = (v, cur) => (v == null ? "—" : `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2 })} ${cur || ""}`);

const CloseForm = ({ oppId, c, onSaved }) => {
  const [f, setF] = useState({ monto_negocio: c.monto_negocio ?? "", comision_monto: c.comision_monto ?? "", moneda: c.moneda || "MXN", pagada: c.pagada });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      onSaved((await api.post(`/red/referidos/${oppId}/cerrar`, { monto_negocio: Number(f.monto_negocio), comision_monto: f.comision_monto === "" ? null : Number(f.comision_monto), moneda: f.moneda, pagada: f.pagada })).data);
      toast.success("Cierre registrado");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="referral-close-form" className="space-y-2 pt-1">
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="close-deal-amount" type="number" min="0" inputMode="decimal" className="c-input text-sm" placeholder="Monto del negocio" value={f.monto_negocio} onChange={set("monto_negocio")} />
        <input data-testid="close-commission-amount" type="number" min="0" inputMode="decimal" className="c-input text-sm" placeholder="Monto de comisión" value={f.comision_monto} onChange={set("comision_monto")} />
      </div>
      <div className="flex items-center gap-3">
        <select data-testid="close-currency" className="c-input text-sm !w-28" value={f.moneda} onChange={set("moneda")}>{["MXN", "USD", "EUR"].map((m) => <option key={m}>{m}</option>)}</select>
        <label className="flex items-center gap-2 text-sm"><input data-testid="close-paid" type="checkbox" className="w-4 h-4 accent-[#bc9538]" checked={f.pagada} onChange={set("pagada")} /> Comisión ya pagada</label>
      </div>
      <button data-testid="close-save-btn" disabled={busy || f.monto_negocio === ""} onClick={save} className="c-btn-primary w-full py-2.5 flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Se cerró el negocio</button>
    </div>
  );
};

const Linker = ({ oppId, acuerdo, onSaved }) => {
  const [v, setV] = useState(acuerdo?.id || "");
  const save = async (val) => { setV(val); try { onSaved((await api.patch(`/red/referidos/${oppId}/acuerdo`, { comision_acuerdo_id: val || null })).data); toast.success("Acuerdo actualizado"); } catch (e) { toast.error(errMsg(e)); } };
  return <div className="space-y-1"><p className="text-xs c-text-2">Acuerdo de comisión ligado</p><AgreementSelect value={v} onChange={save} testid="referral-agreement-select" /></div>;
};

export const ReferralCard = ({ oppId, onChanged }) => {
  const [r, setR] = useState(null);
  const [editing, setEditing] = useState(false);
  const load = useCallback(() => api.get(`/red/referidos/${oppId}`).then((x) => setR(x.data)).catch(() => {}), [oppId]);
  useEffect(() => { load(); }, [load]);
  if (!r) return null;
  const c = r.cierre;
  const saved = (d) => { setR(d); setEditing(false); onChanged?.(); };
  return (
    <div data-testid="referral-card" className="c-card p-4 space-y-2.5 border-[#bc9538]/50">
      <p className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase c-accent"><Network className="w-3.5 h-3.5" /> Referido de la red CONECTA</p>
      <p data-testid="referral-counterpart" className="text-sm">{r.rol === "dueno" ? "Presentado a" : "Recibido de"} <Link to={`/conecta/u/${r.contraparte.id}`} className="font-semibold c-accent">{r.contraparte.nombre_publico}</Link> · {r.servicio_nombre}</p>
      <Link to={`/conecta/solicitudes/${r.intro_request_id}`} data-testid="referral-request-link" className="text-xs font-semibold c-accent">Ver solicitud</Link>
      {r.rol === "dueno" && <Linker oppId={oppId} acuerdo={r.acuerdo} onSaved={saved} />}
      {c.cerrado && !editing ? (
        <div data-testid="referral-closed" className="rounded-xl bg-emerald-500/10 p-3 text-sm space-y-0.5">
          <p className="font-semibold flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400"><CheckCircle2 className="w-4 h-4" /> Se cerró el negocio</p>
          <p>Negocio: <span data-testid="referral-closed-amount">{money(c.monto_negocio, c.moneda)}</span> · Comisión: <span data-testid="referral-closed-commission">{money(c.comision_monto, c.moneda)}</span> · {c.pagada ? "Pagada" : "Por cobrar"}</p>
          <p className="text-xs c-muted">Marcado por {c.cerrado_por}</p>
          <button data-testid="referral-edit-close" onClick={() => setEditing(true)} className="text-xs font-semibold c-accent">Editar cierre</button>
        </div>
      ) : <CloseForm oppId={oppId} c={c} onSaved={saved} />}
    </div>
  );
};
